import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { PackagesService } from '../../services/payments/packages/packages.service';
import { PackagesResponse } from '../../models/payment/packagesResponse';
import { OrderSummary } from '../../models/psychometric-test/psychometric-test.model';
import { BillingComponent } from '../psychometric-test/billing/billing.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { DurationTypeEnum } from '../../models/payment/package-enums.model';

@Component({
    selector: 'app-shop-billing',
    standalone: true,
    imports: [CommonModule, BillingComponent, ProgressSpinnerModule, ButtonModule],
    templateUrl: './shop-billing.component.html',
    styleUrls: ['./shop-billing.component.scss']
})
export class ShopBillingComponent implements OnInit {
    loading = true;
    orderSummary: OrderSummary | null = null;
    packageId: number | null = null;
    error = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private packagesService: PackagesService,
        private messageService: MessageService
    ) { }

    ngOnInit(): void {
        this.route.queryParamMap.subscribe(params => {
            const id = params.get('packageId');
            if (id) {
                this.packageId = Number(id);
                this.loadPackage(this.packageId);
            } else {
                this.loading = false;
                this.error = true;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No package selected.' });
            }
        });
    }

    loadPackage(id: number): void {
        this.loading = true;
        this.packagesService.getActivePackages().subscribe({
            next: (res: any) => {
                const packagesArray: PackagesResponse[] = res.result || [];
                const packageData = packagesArray.find(p => p.id === id);
                if (packageData) {
                    this.orderSummary = this.mapPackageToOrderSummary(packageData);
                    this.loading = false;
                } else {
                    this.handleError();
                }
            },
            error: () => this.handleError()
        });
    }

    private handleError(): void {
        this.loading = false;
        this.error = true;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load package details.' });
    }

    private mapPackageToOrderSummary(pkg: PackagesResponse): OrderSummary {
        const durationMonths = pkg.duration ?? 1;
        const isSingle = Number(durationMonths) === 1;
        const durationText = pkg.durationType === DurationTypeEnum.Days
            ? `${durationMonths} ${isSingle ? 'Day' : 'Days'}`
            : `${durationMonths} ${isSingle ? 'Month' : 'Months'}`;

        const subtotal = pkg.amount || 0;
        const gstRate = 0.18;
        const tax = +(subtotal * gstRate).toFixed(2);
        const total = +(subtotal + tax).toFixed(2);

        return {
            packageId: pkg.id,
            testName: pkg.name,
            description: pkg.description || '',
            duration: durationText,
            subtotal,
            tax,
            total,
            whatsIncluded: pkg.servicesIncluded || [],
        };
    }

    goBack(): void {
        const targetRoute = this.router.url.includes('/portal') ? '/portal/shop' : '/shop';
        this.router.navigate([targetRoute]);
    }
}
